/**
 * The Recommendation Letter Generator: a letter of recommendation assembled from the
 * writer's own observations.
 *
 * The tool writes none of the substance. The relationship, the strengths, the examples,
 * the comparison and the limits are the writer's; the template supplies the frame round
 * them (the opening, the connecting sentences, the endorsement) and flags what is still
 * blank or too thin to persuade. A comparison is included only if the writer gives a
 * group and a level, and an endorsement is whatever level the writer chooses.
 *
 * Frame syntax is documented in `correspondence/compose.ts`. The bank is spread into the
 * widget's strings, so a translation can override any sentence.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  asFragment,
  asSentence,
  cleanBlock,
  cleanLine,
  compose,
  countWords,
  orPlaceholder,
} from "@/lib/tool-kit/correspondence/compose";

export const ENDORSEMENTS = [
  "strongest",
  "strong",
  "solid",
  "qualified",
] as const;
export type Endorsement = (typeof ENDORSEMENTS)[number];

export const RANKS = ["none", "top1", "top5", "top10", "top25", "own"] as const;
export type Rank = (typeof RANKS)[number];

export const REC_SIGN_OFFS = [
  "sincerely",
  "bestRegards",
  "yoursSincerely",
] as const;
export type RecSignOff = (typeof REC_SIGN_OFFS)[number];

export const EVIDENCE_SLOTS = 3;

/** Under this many words an observation is rarely specific enough to persuade. */
export const MIN_EVIDENCE_WORDS = 20;
/** A one-page letter: below this it is thin, above it the reader skims. */
export const LETTER_SHORT_WORDS = 250;
export const LETTER_LONG_WORDS = 800;

export interface EvidenceItem {
  quality: string;
  observation: string;
}

export interface RecommendationInput {
  /** Already formatted for the reader's locale, or "" for no date line. */
  date: string;
  recipient: string;
  name: string;
  shortName: string;
  purpose: string;
  capacity: string;
  duration: string;
  context: string;
  evidence: EvidenceItem[];
  rank: Rank;
  cohort: string;
  basis: string;
  ownComparison: string;
  growth: string;
  limit: string;
  endorsement: Endorsement;
  signName: string;
  signTitle: string;
  signInstitution: string;
  signEmail: string;
  signOff: RecSignOff;
}

export const EMPTY_EVIDENCE: EvidenceItem[] = Array.from(
  { length: EVIDENCE_SLOTS },
  () => ({ quality: "", observation: "" })
);

export const RECOMMENDATION_BANK = {
  greeting: "Dear {recipient},",
  greetingNoName: "To the admissions committee,",
  signOffs: {
    sincerely: "Sincerely,",
    bestRegards: "Best regards,",
    yoursSincerely: "Yours sincerely,",
  },
  ranks: {
    top1: "top 1%",
    top5: "top 5%",
    top10: "top 10%",
    top25: "top 25%",
  },
  placeholders: {
    name: "Student's name",
    capacity:
      "how you know the student, such as “their instructor in two courses”",
    duration: "how long",
    observation: "What you observed, with one specific example",
    cohort: "the group you are comparing with, and its size",
    limit: "the reservation you want the reader to know about",
    signName: "Your name",
    signTitle: "Your title",
    signInstitution: "Your institution",
  },
  intro: [
    "?purpose:I am writing in support of {name}’s application for {purpose}.",
    "!purpose:I am writing in support of {name}’s application.",
    "I have known {short} for {duration}, as {capacity}.",
    "?context:{context}",
  ],
  evidenceLead: [
    "The strength that stands out first is {quality}.",
    "A second strength is {quality}.",
    "A third is {quality}.",
  ],
  comparison: [
    "?rank?basis:Compared with {cohort}, {short} is in the {rank}, judged by {basis}.",
    "?rank!basis:Compared with {cohort}, {short} is in the {rank}.",
    "?own:{ownComparison}",
  ],
  growth: ["?growth:{growth}"],
  limit: ["?limit:One area where {short} is still developing is {limit}."],
  endorsement: [
    "?strongest:I recommend {name} without reservation.",
    "?strong:I recommend {name} strongly.",
    "?solid:I am pleased to recommend {name}.",
    "?qualified:I recommend {name}, with one qualification: {limit}.",
  ],
  closing: [
    "?email:Please contact me at {email} if I can provide any further information.",
    "!email:Please contact me if I can provide any further information.",
  ],
} as const;

export interface RecommendationBank {
  greeting: string;
  greetingNoName: string;
  signOffs: Readonly<Record<RecSignOff, string>>;
  ranks: Readonly<Record<"top1" | "top5" | "top10" | "top25", string>>;
  placeholders: Readonly<Record<string, string>>;
  intro: readonly string[];
  evidenceLead: readonly string[];
  comparison: readonly string[];
  growth: readonly string[];
  limit: readonly string[];
  endorsement: readonly string[];
  closing: readonly string[];
}

function evidenceParagraphs(
  input: RecommendationInput,
  bank: RecommendationBank
): string[] {
  const out: string[] = [];
  input.evidence.slice(0, EVIDENCE_SLOTS).forEach((item, index) => {
    const quality = asFragment(item.quality);
    const observation = cleanBlock(item.observation);
    // The first item is always shown: a letter needs at least one observation.
    if (index > 0 && quality === "" && observation === "") {
      return;
    }
    const sentences: string[] = [];
    if (quality !== "") {
      sentences.push(t(bank.evidenceLead[index] ?? "", { quality }));
    }
    sentences.push(
      orPlaceholder(
        asSentence(observation),
        bank.placeholders.observation ?? ""
      )
    );
    out.push(sentences.join(" "));
  });
  return out;
}

function rankLabel(input: RecommendationInput, bank: RecommendationBank) {
  if (input.rank === "none" || input.rank === "own") {
    return "";
  }
  return bank.ranks[input.rank];
}

/** The letter as marked paragraphs, date and greeting first, signature last. */
export function buildRecommendationLetter(
  input: RecommendationInput,
  bank: RecommendationBank
): string[] {
  const ph = (key: string) => bank.placeholders[key] ?? key;
  const name = orPlaceholder(cleanLine(input.name), ph("name"));
  const short = cleanLine(input.shortName) || name;
  const limit = asFragment(input.limit);
  const email = cleanLine(input.signEmail);
  const values: Record<string, string> = {
    name,
    short,
    purpose: asFragment(input.purpose),
    capacity: orPlaceholder(asFragment(input.capacity), ph("capacity")),
    duration: orPlaceholder(asFragment(input.duration), ph("duration")),
    context: asSentence(input.context),
    rank: rankLabel(input, bank),
    cohort: orPlaceholder(asFragment(input.cohort), ph("cohort")),
    basis: asFragment(input.basis),
    ownComparison: asSentence(input.ownComparison),
    growth: asSentence(input.growth),
    limit:
      input.endorsement === "qualified"
        ? orPlaceholder(limit, ph("limit"))
        : limit,
    email,
  };
  const flags = new Set<string>([input.endorsement]);
  const set = (flag: string, on: boolean) => {
    if (on) {
      flags.add(flag);
    }
  };
  set("purpose", values.purpose !== "");
  set("context", values.context !== "");
  set("rank", values.rank !== "");
  set("basis", values.basis !== "");
  set("own", input.rank === "own" && values.ownComparison !== "");
  set("growth", values.growth !== "");
  set("limit", limit !== "" && input.endorsement !== "qualified");
  set("email", email !== "");

  const paragraphs: string[] = [];
  const date = cleanLine(input.date);
  if (date !== "") {
    paragraphs.push(date);
  }
  const recipient = cleanLine(input.recipient);
  paragraphs.push(
    recipient ? t(bank.greeting, { recipient }) : bank.greetingNoName
  );
  paragraphs.push(...compose(bank.intro, values, flags));
  paragraphs.push(...evidenceParagraphs(input, bank));
  for (const frames of [bank.comparison, bank.growth, bank.limit]) {
    paragraphs.push(...compose(frames, values, flags));
  }
  paragraphs.push(
    [
      ...compose(bank.endorsement, values, flags),
      ...compose(bank.closing, values, flags),
    ].join(" ")
  );
  const signature = [
    bank.signOffs[input.signOff],
    orPlaceholder(cleanLine(input.signName), ph("signName")),
    orPlaceholder(cleanLine(input.signTitle), ph("signTitle")),
    orPlaceholder(cleanLine(input.signInstitution), ph("signInstitution")),
  ];
  paragraphs.push(signature.join("\n"));
  return paragraphs;
}

export type ReviewNote =
  | { id: "evidence-thin"; slot: number; words: number }
  | { id: "no-evidence" }
  | { id: "comparison-no-group" }
  | { id: "qualified-no-limit" }
  | { id: "no-relationship" }
  | { id: "letter-short"; words: number }
  | { id: "letter-long"; words: number };

/**
 * What a careful reader would question, from the form alone: thin observations, a
 * comparison with no group behind it, a qualified endorsement that says nothing, a
 * letter too short or too long. It cannot judge the writing, only the gaps.
 */
export function reviewLetter(
  input: RecommendationInput,
  paragraphs: readonly string[]
): ReviewNote[] {
  const notes: ReviewNote[] = [];
  const filled = input.evidence
    .slice(0, EVIDENCE_SLOTS)
    .map((item, slot) => ({ slot, words: countWords(item.observation) }))
    .filter((item) => item.words > 0);
  if (filled.length === 0) {
    notes.push({ id: "no-evidence" });
  }
  for (const item of filled) {
    if (item.words < MIN_EVIDENCE_WORDS) {
      notes.push({
        id: "evidence-thin",
        slot: item.slot + 1,
        words: item.words,
      });
    }
  }
  if (
    input.rank !== "none" &&
    input.rank !== "own" &&
    cleanLine(input.cohort) === ""
  ) {
    notes.push({ id: "comparison-no-group" });
  }
  if (input.endorsement === "qualified" && cleanLine(input.limit) === "") {
    notes.push({ id: "qualified-no-limit" });
  }
  if (cleanLine(input.capacity) === "" || cleanLine(input.duration) === "") {
    notes.push({ id: "no-relationship" });
  }
  const words = countWords(paragraphs.join("\n"));
  if (words < LETTER_SHORT_WORDS) {
    notes.push({ id: "letter-short", words });
  } else if (words > LETTER_LONG_WORDS) {
    notes.push({ id: "letter-long", words });
  }
  return notes;
}
